import axios from "axios";
import { Storage } from "@/services/Storage";
import router from "@/router";
// Stable cart line id for BE SeperateLine UPSERT-by-id (Path 1)
// docs: FRONTEND.md cart line identity; sales-module addToCurrentSaleLines
// Same fix as Restaurant POS cc7dfec + 6ba07b4 (await hydrate, no blind mint).
import { generateUUID } from "@/utils/uuid";
// Drop voided/archived lines from table save payloads (same contract as invoice).
// docs: src/utils/activeSaleLines.js; docs/FRONTEND.md
import { filterActiveSaleLines } from "@/utils/activeSaleLines";
import { responses } from "@/utils/responses";
import connectivityMonitor from "@/services/connectivityMonitor";

/**
 * Normalize server table payload item arrays (savetable / tables list shapes).
 * Why: BE returns items under different keys; hydrate must not miss ids.
 * @param {object|null|undefined} serverTable
 * @returns {Array}
 */
function extractServerTableItems(serverTable) {
    if (!serverTable || typeof serverTable !== 'object') return [];
    if (Array.isArray(serverTable.items)) return serverTable.items;
    if (Array.isArray(serverTable.Items)) return serverTable.Items;
    if (Array.isArray(serverTable.saleLines)) return serverTable.saleLines;
    return [];
}

/** Matches OnlineSalesServer tables list `limit` (max open tables). Ticket 25935. */
const TABLES_LIST_LIMIT_KEY = 'chrilan.tablesListLimit';
const DEFAULT_TABLES_LIST_LIMIT = 10000;
const MIN_TABLES_LIST_LIMIT = 1;
const MAX_TABLES_LIST_LIMIT = 100000;

function readTablesListLimit() {
    try {
        const n = parseInt(localStorage.getItem(TABLES_LIST_LIMIT_KEY), 10);
        if (Number.isFinite(n) && n >= MIN_TABLES_LIST_LIMIT && n <= MAX_TABLES_LIST_LIMIT) {
            return n;
        }
    } catch (_) { /* ignore */ }
    return DEFAULT_TABLES_LIST_LIMIT;
}

function writeTablesListLimit(n) {
    try {
        localStorage.setItem(TABLES_LIST_LIMIT_KEY, String(n));
    } catch (_) { /* ignore */ }
}

function normalizeTablesListLimit(raw) {
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < MIN_TABLES_LIST_LIMIT) return MIN_TABLES_LIST_LIMIT;
    if (n > MAX_TABLES_LIST_LIMIT) return MAX_TABLES_LIST_LIMIT;
    return n;
}

export default {
    name: 'tables',
    state: {
        allTables: [],
        isTabOpen: false,
        openTableName: null,
        activeSheet: null,
        tablesListLimit: readTablesListLimit(),
    },
    getters: {
        getAllTables: (state) => state.allTables,
        getIsTabOpen: (state) => state.isTabOpen,
        getActiveSheet: (state) => state.activeSheet,
        getOpenTableName: (state) => state.openTableName,
        getTablesListLimit: (state) => state.tablesListLimit,
        getTableBySaleNum: (state) => (saleNum) => (state.allTables) ? state.allTables.find(s => s.saleNumber === saleNum) : undefined,
    },

    mutations: {
        setAllTables: (state, payload) => {
            if (payload) {
                return state.allTables = payload
            } else {
                return Storage.getAllTables().then(res => state.allTables = (res ? res : []))
            }
        },

        setIsTabOpen: (state, data) => state.isTabOpen = data,
        setOpenTableName: (state, data) => state.openTableName = data,
        setTablesListLimit: (state, limit) => {
            const n = normalizeTablesListLimit(limit);
            state.tablesListLimit = n;
            writeTablesListLimit(n);
        },
    },
    actions: {
        /**
         * Load open quotations/tables for the list screen.
         * @param {*} context
         * @param {{ limit?: number }|number|undefined} payload optional max open tables override
         */
        getAllTables: async (context, payload) => {
            let loggedIn = await auth.isCashierLoggedIn();
            if (loggedIn) {
                let store = await Storage.getDefaultStore();
                let user = await Storage.getCurrentUser()
                let limit = context.state.tablesListLimit;
                if (payload != null) {
                    if (typeof payload === 'number') {
                        limit = normalizeTablesListLimit(payload);
                        context.commit('setTablesListLimit', limit);
                    } else if (typeof payload === 'object' && payload.limit != null) {
                        limit = normalizeTablesListLimit(payload.limit);
                        context.commit('setTablesListLimit', limit);
                    }
                }
                return axios.get(serverApi + `tables/${store.StoreDB}/${user.Username}`, {
                    params: { limit },
                })
                    .then(({ data }) => {
                        context.commit('setAllTables', data)
                        return data;
                    }).catch((err) => errorHandler.tomcatError(err))
            }
        },
        saveNewTable: async (context, payload) => {
            let settings = await Storage.getDeviceSettings();
            // Only active (non-voided) lines — voided rows stay archived on BE and must not re-UPSERT.
            // Empty active set is allowed so "void all then save" does not error.
            // docs: src/utils/activeSaleLines.js
            let saleLines = filterActiveSaleLines(context.getters.getSaleLines);
            for (let i = 0; i < saleLines.length; i++) {
                let line = saleLines[i];
                saleLines[i].Line = (Number(line.Qty) * Number(line.Unit)) - Number(line.Discount);
            }
            let obj = {
                SaleNum: context.getters.getCurrentSale,
                Name: context.getters.getOpenTableName,
                User: context.getters.getCurrentUser.Username,
                PC: settings.PC,
                items: saleLines,
                Customer: payload.Customer,
                discount: parseFloat(context.getters.getSaleDiscount.total),
            }
            return context.dispatch('AddOrUpdateTable', obj)
                .catch(err => {
                    throw new Error(err)
                });
        },

        createNewTable: async (context, payload) => {
            let user = await Storage.getCurrentUser();
            let settings = await Storage.getDeviceSettings();
            let obj = {
                SaleNum: 0,
                Name: (payload.name && payload.name.length > 0) ? payload.name : "Table #" + context.getters.getCurrentSale,
                User: user.Username,
                PC: settings.PC,
                items: [],
                Customer: payload.Customer,
                discount: 0,
            }
            return context.dispatch('AddOrUpdateTable', obj)
                .then((res) => res)
                .catch(err => errorHandler.tomcatError(err));
        },
        AddOrUpdateTable: async (context, payload) => {
            let isAllowed = await context.dispatch(`validateSecurityPermission`, `SaveTable`);
            if (!isAllowed) throw new Error(`User not allowed to Save Tables`);
            let store = await Storage.getDefaultStore();
            // Stamp stable client id + 1-indexed saleline on every line before
            // POST /tables/{DB}. Backend SeperateLine Path 1 (UPSERT by id)
            // requires both; without them Path 3 PLU-only match collapses
            // same-PLU multi-price lines into one row.
            // docs: FRONTEND.md cart line identity; BE CurrentSale.SeperateLine
            // Restaurant 6ba07b4: await line-id hydrate before mint; never blind-mint
            // existing online sales (doubling: new UUID → BE INSERT while old row stays).
            if (payload && Array.isArray(payload.items)) {
                const isOnline = connectivityMonitor.getStatus();
                const identitySaleNum = Number(
                    payload.SaleNum || payload.saleNumber || 0
                );
                const isExistingSaleIdentity =
                    Number.isFinite(identitySaleNum) && identitySaleNum > 0;
                const lineHasIdentity = (it) =>
                    !!(it && (it.id || it.Id || it.lineId || it.currentSaleLineId));

                // Online existing sale: hydrate server PKs before any UUID mint.
                if (isOnline && isExistingSaleIdentity) {
                    const needsLineIdentityHydrate = payload.items.some(
                        (it) => it && !lineHasIdentity(it)
                    );
                    if (needsLineIdentityHydrate) {
                        try {
                            const serverTable = await context.dispatch(
                                'getTableData',
                                identitySaleNum
                            );
                            const detailItems = extractServerTableItems(serverTable);
                            if (Array.isArray(detailItems) && detailItems.length > 0) {
                                // PLU+desc then PLU then index — same match order as Restaurant.
                                const byKey = new Map();
                                detailItems.forEach((line, idx) => {
                                    if (!line) return;
                                    const plu = String(line.Plu || line.plu || '');
                                    const desc = String(
                                        line.Description || line.description || ''
                                    );
                                    const keyExact = `${plu}::${desc}`;
                                    if (!byKey.has(keyExact)) byKey.set(keyExact, []);
                                    byKey.get(keyExact).push(line);
                                    const keyPlu = `${plu}::`;
                                    if (!byKey.has(keyPlu)) byKey.set(keyPlu, []);
                                    byKey.get(keyPlu).push(line);
                                    byKey.set(`idx::${idx}`, line);
                                });
                                payload.items.forEach((item, idx) => {
                                    if (!item || lineHasIdentity(item)) return;
                                    const plu = String(item.Plu || item.plu || '');
                                    const desc = String(
                                        item.Description || item.description || ''
                                    );
                                    const exactBucket = byKey.get(`${plu}::${desc}`) || [];
                                    const pluBucket = byKey.get(`${plu}::`) || [];
                                    const match =
                                        exactBucket.shift() ||
                                        pluBucket.shift() ||
                                        byKey.get(`idx::${idx}`);
                                    if (!match) return;
                                    const id =
                                        match.id ||
                                        match.Id ||
                                        match.lineId ||
                                        match.currentSaleLineId ||
                                        null;
                                    const saleline =
                                        match.saleline != null
                                            ? match.saleline
                                            : match.SaleLine != null
                                              ? match.SaleLine
                                              : match.saleLine != null
                                                ? match.saleLine
                                                : null;
                                    if (id) {
                                        item.id = id;
                                    }
                                    if (saleline != null && item.saleline == null) {
                                        item.saleline = saleline;
                                    }
                                });
                            }
                        } catch (hydrateIdentityErr) {
                            console.error(
                                '[TableFlow][AddOrUpdateTable:lineIdentityHydrateFailed]',
                                hydrateIdentityErr
                            );
                            const err = new Error(
                                'Could not identify all lines. Please try again or reopen the table.'
                            );
                            err.code = 'LINE_IDENTITY_HYDRATE_FAILED';
                            try {
                                responses.showError(
                                    'Could not identify all lines. Please try again or reopen the table.'
                                );
                            } catch (_) { /* toast optional */ }
                            throw err;
                        }

                        // After hydrate, remaining missing-id lines must not be minted.
                        const stillMissingIdentity = payload.items.filter(
                            (it) => it && !lineHasIdentity(it)
                        );
                        if (stillMissingIdentity.length > 0) {
                            console.error(
                                '[TableFlow][AddOrUpdateTable:lineIdentityStillMissing]',
                                {
                                    saleNum: identitySaleNum,
                                    missingCount: stillMissingIdentity.length,
                                }
                            );
                            const err = new Error(
                                'Some lines could not be identified. Please reopen the table and try again.'
                            );
                            err.code = 'LINE_IDENTITY_MISSING';
                            try {
                                responses.showError(
                                    'Some lines could not be identified. Please reopen the table and try again.'
                                );
                            } catch (_) { /* toast optional */ }
                            throw err;
                        }
                    }
                }

                payload.items.forEach((item, i) => {
                    if (!item) return;
                    if (!item.id && !item.Id && !item.lineId && !item.currentSaleLineId) {
                        // Safety: online existing sale never reaches here with missing ids.
                        // Mint only new-table / offline paths.
                        if (isOnline && isExistingSaleIdentity) {
                            const err = new Error(
                                'Cannot save — lines missing identity. Reopen the table and try again.'
                            );
                            err.code = 'LINE_IDENTITY_MISSING';
                            try {
                                responses.showError(
                                    'Cannot save — lines missing identity. Reopen the table and try again.'
                                );
                            } catch (_) { /* toast optional */ }
                            throw err;
                        }
                        item.id = generateUUID();
                    } else if (!item.id) {
                        item.id = item.Id || item.lineId || item.currentSaleLineId;
                    }
                    if (item.saleline == null && item.SaleLine == null && item.saleLine == null) {
                        item.saleline = i + 1;
                    } else if (item.saleline == null) {
                        item.saleline = item.SaleLine != null ? item.SaleLine : item.saleLine;
                    }
                });
            }
            return axios.post(serverApi + `tables/${store.StoreDB}`, payload)
                .then(({ data }) => {
                    context.commit('clearSale');
                    context.dispatch('setCustomer', "");
                    context.commit('setLPO', {
                        debtorID: ``,
                        lpoNumber: undefined,
                    });
                    return data
                }).catch((err) => errorHandler.tomcatError(err))
        },
        updateTable: async (context, payload) => {
            // Same active-line filter as saveNewTable / invoice (voided must not re-enter payload).
            // docs: src/utils/activeSaleLines.js
            let saleLines = filterActiveSaleLines(context.getters.getSaleLines);
            let settings = await Storage.getDeviceSettings();
            let user = await Storage.getCurrentUser();
            for (let i = 0; i < saleLines.length; i++) {
                let line = saleLines[i];
                saleLines[i].saleLine = i + 1;
                saleLines[i].Line = (Number(line.Qty) * Number(line.Unit)) - Number(line.Discount);
            }
            let data = {
                SaleNum: context.getters.getCurrentSale,
                Name: (payload.name && payload.name.length > 0) ? payload.name : "Table #" + context.getters.getCurrentSale,
                User: user.Username,
                PC: settings.PC,
                items: saleLines,
                Customer: payload.Customer ? payload.Customer : context.getters.getCustomer,
                discount: parseFloat(context.getters.getSaleDiscount.total),
            };
            return context.dispatch('AddOrUpdateTable', data)
        },
        removeTable: async (context, table) => {
            let settings = await Storage.getDeviceSettings();
            context.commit(`setOverrideDetails`, table.Name)
            let isAllowed = await context.dispatch(`validateSecurityPermission`, `VoidAll`);
            if (!isAllowed) return false;
            let store = await Storage.getDefaultStore();
            if (settings.PrintVoid) {
                let user = await Storage.getCurrentUser();
                const items = table.items.map((item) => {
                    let description = item.Description;
                    let plu = item.Plu;
                    let qty = item.Qty;
                    let value = item.Line;
                    return {
                        description,
                        plu,
                        qty,
                        value
                    }
                });
                const data = {
                    supervisor: isAllowed.supervisor,
                    items: items,
                    pc: settings.PC,
                    user: user.Username,
                    reason: 'Sale Number' + ' ' + table.saleNumber
                }
                await context.dispatch('printVoid', data);
            }
            return axios.delete(serverApi + `tables/${store.StoreDB}/void/${table.saleNumber}`)
                .then(() => true).catch((err) => errorHandler.tomcatError(err))
        },
        getTableData: async (context, saleNumber) => {
            let store = await Storage.getDefaultStore();
            return axios.get(franchiseTomcatApi + `savetable/${store.StoreDB}/${saleNumber}`)
                .then(({ data }) => data)
                .catch(err => errorHandler.tomcatError(err))
        },
        getTableBySaleNumber: async (context, saleNumber) => {
            return context.dispatch(`getTableData`, saleNumber).then(res => {
                let tabs = []
                return Promise.all([
                    context.dispatch(`openTable`, res),
                ]).then(() => res)
            }).catch(() => router.push({ name: `tables` }))
        },

        getOpenTables: async () => {
            let store = await Storage.getDefaultStore();
            return axios.get(serverApi + `tables/${store.StoreDB}/getAllOpenTables`)
                .then(({ data }) => data).catch(err => errorHandler.tomcatError(err))
        },

        mergeTables: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            let user = await Storage.getCurrentUser();
            payload.mergedBy = user.Username;
            return axios.post(serverApi + `tables/${store.StoreDB}/merge`, payload)
                .catch(err => errorHandler.tomcatError(err))
        },

        transferTable: async (context, payload) => {
            let user = await Storage.getCurrentUser();
            user = user.Username;
            let isAllowed = await context.dispatch(`validateSecurityPermission`, `transferTable`)
            if (!isAllowed) return false
            $utils.showLoading();
            let store = await Storage.getDefaultStore();
            payload.transferredBy = user;
            return axios.put(serverApi + `tables/${store.StoreDB}/transfer`, payload)
                .then(() => true)
                .catch(err => errorHandler.tomcatError(err))
                .finally(() => $utils.hideLoading())
        },
        initiateEmptyTable: async () => {
            let user = await Storage.getCurrentUser();
            user = user.Username;

            let settings = await Storage.getDeviceSettings();
            let pc = settings.PC;
            let store = await Storage.getDefaultStore();
            let storeDb = store.StoreDB;
            let obj = {
                User: user,
                PC: pc,
            }

            return axios.post(serverApi + `tables/${storeDb}/initiateEmptyTable`, obj).then(({ data }) => data)
                .catch(err => errorHandler.tomcatError(err))
        },
        async splitTable(context, payload) {
            let store = await Storage.getDefaultStore();
            let storeDb = store.StoreDB;

            let user = await Storage.getCurrentUser();
            user = user.Username;
            let obj = {
                splitBy: user,
                ...payload
            };

            return axios.post(serverApi + `tables/${storeDb}/split`, obj)
                .then(() => context.commit('setCurrentLines', []))
                .catch(err => errorHandler.tomcatError(err))
        },
        proceedToSales(context) {
            let settings = context.getters.getDeviceSettings;
            let autoLogout = settings.AutoLog === 1;
            const path = router.currentRoute.path;
            if (autoLogout || settings.PosType === `WaiterStation`) {
                if (path === '/poslogin') return;
                return context.dispatch("posLogout");
            }
            if (settings.PosType === `Retail` || settings.PosType === `Touch`) {
                if (path === '/pos') return;
                return router.push({ name: `pos` })
            }
            if (path === '/tables') return;
            return router.push({ name: `tables` })
        },
        async checkForOpenTables({ commit, rootState }) {
            try {
                let user = await Storage.getCurrentUser();
                let store = await Storage.getDefaultStore();
                let storeDb = store.StoreDB;

                user = user.Username;

                const response = await axios.get(serverApi + `tables/${storeDb}/countOpenTables/${user}`);
                if (response.data > 0) {
                    return {
                        hasOpenTables: true,
                        count: response.data
                    };
                }
                return {
                    hasOpenTables: false,
                    count: 0
                };
            } catch (error) {
                console.error('Error checking for open tables:', error);
                throw error;
            }
        },
    }
}
